"use client";

import React, { useEffect } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import Breadcrumb from '@/components/Breadcrumb';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll('.reveal');
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => {if (e.isIntersecting) {e.target.classList.add('visible');obs.unobserve(e.target);}}),
      { threshold: 0.1 }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);
}

interface ServiceItem {
  id: string;
  number: string;
  title: string;
  description: string;
  features: string[];
  icon: string;
  img: string;
  imgAlt: string;
  accent?: boolean;
}

const servicesList: ServiceItem[] = [
{
  id: 'brand-strategy',
  number: '01',
  title: 'Brand Strategy',
  description: 'We develop positioning frameworks, naming systems, and visual identities that make your brand the undeniable choice. From discovery workshops to brand books — we deliver clarity at every touchpoint.',
  features: ['Market positioning', 'Visual identity systems', 'Brand voice & messaging', 'Competitor analysis', 'Launch playbooks'],
  icon: 'SparklesIcon',
  img: "https://img.rocket.new/generatedImages/rocket_gen_img_118d992ce-1772126101337.png",
  imgAlt: 'Team collaborating on brand strategy with mood boards and color swatches pinned to a wall'
},
{
  id: 'digital-transformation',
  number: '02',
  title: 'Digital Transformation',
  description: 'End-to-end digital overhauls — from legacy system modernization to AI-powered workflow automation. We map your current state, design your future state, and execute with precision.',
  features: ['Tech stack audits', 'Process automation', 'Platform migrations', 'AI integration', 'Performance optimization'],
  icon: 'ComputerDesktopIcon',
  img: "https://img.rocket.new/generatedImages/rocket_gen_img_11113d5d4-1772110819274.png",
  imgAlt: 'Developer reviewing digital transformation dashboard with multiple data visualization screens',
  accent: true
},
{
  id: 'creative-production',
  number: '03',
  title: 'Creative Production',
  description: 'Film, photography, motion design, and interactive content — produced in-house by our creative studio. Every asset is built to perform across every channel your audience uses.',
  features: ['Brand films', 'Photography', 'Motion design', 'Social content', 'Campaign assets'],
  icon: 'FilmIcon',
  img: "https://img.rocket.new/generatedImages/rocket_gen_img_16a57afeb-1772160810415.png",
  imgAlt: 'Creative director reviewing video production footage with a camera crew in a professional studio'
},
{
  id: 'growth-consulting',
  number: '04',
  title: 'Growth Consulting',
  description: 'Data-driven growth strategy for companies ready to scale. We identify your highest-leverage opportunities and build the systems to capture them — from acquisition to retention.',
  features: ['Growth audits', 'Funnel optimization', 'Retention programs', 'Revenue modeling', 'KPI frameworks'],
  icon: 'ChartBarIcon',
  img: "https://img.rocket.new/generatedImages/rocket_gen_img_1c3895ecc-1764656426788.png",
  imgAlt: 'Business analyst presenting growth metrics and charts on a large screen in a conference room'
},
{
  id: 'ux-design',
  number: '05',
  title: 'UX & Product Design',
  description: 'User experiences that reduce friction and increase delight. From research and wireframes through to pixel-perfect UI — we design products people genuinely love to use.',
  features: ['User research', 'Journey mapping', 'Prototyping', 'UI design systems', 'Usability testing'],
  icon: 'CursorArrowRaysIcon',
  img: "https://images.unsplash.com/photo-1576595580361-90a855b84b20",
  imgAlt: 'UX designer sketching wireframes and user flow diagrams on a whiteboard'
},
{
  id: 'content-marketing',
  number: '06',
  title: 'Content Marketing',
  description: 'Strategic content that builds authority and drives organic growth. We create editorial calendars, long-form articles, thought leadership, and distribution strategies that compound over time.',
  features: ['Content strategy', 'SEO content', 'Thought leadership', 'Distribution playbooks', 'Performance tracking'],
  icon: 'DocumentTextIcon',
  img: "https://img.rocket.new/generatedImages/rocket_gen_img_18e14294d-1772192501563.png",
  imgAlt: 'Content strategist working on editorial calendar with laptop and notebook at a bright desk'
}];


const processSteps = [
{ num: '01', title: 'Discovery', desc: 'Deep-dive workshops to understand your business, audience, and competitive landscape.' },
{ num: '02', title: 'Strategy', desc: 'We build a bespoke roadmap — no templates, no shortcuts, just clear direction.' },
{ num: '03', title: 'Execution', desc: 'Our team delivers in focused sprints with weekly check-ins and transparent reporting.' },
{ num: '04', title: 'Launch', desc: 'Coordinated rollout with measurement frameworks built in from day one.' },
{ num: '05', title: 'Optimise', desc: 'Ongoing refinement based on real data — because good work gets better over time.' }];


export default function ServicesPage() {
  useReveal();

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            "itemListElement": servicesList.map((s, i) => ({
              "@type": "Service",
              "position": i + 1,
              "name": s.title,
              "description": s.description,
              "provider": { "@type": "Organization", "name": "BusinessSite" }
            }))
          })
        }} />
      

      <Header />

      <main className="bg-background overflow-x-hidden">

        {/* ── LIGHTWEIGHT HERO ── */}
        <section
          className="relative pt-32 pb-20 px-6 md:px-12 overflow-hidden"
          style={{ background: 'var(--color-primary)' }}>
          
          {/* Atmospheric gradient */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: 'radial-gradient(ellipse 80% 60% at 60% 50%, rgba(201,80,42,0.18) 0%, transparent 70%)'
            }} />
          
          <div className="relative z-10" style={{ maxWidth: 'var(--container-max)', margin: '0 auto' }}>
            <div className="mb-8">
              <Breadcrumb crumbs={[{ label: 'Services' }]} />
            </div>
            <div className="max-w-3xl reveal">
              <span className="section-label mb-6 block" style={{ color: 'rgba(255,255,255,0.5)' }}>
                What We Offer
              </span>
              <h1
                className="fluid-h1 font-display font-light leading-none text-white mb-6"
                style={{ fontFamily: 'var(--font-display)' }}>
                
                Services built<br />
                <em className="not-italic text-accent">to move</em> the needle.
              </h1>
              <p className="text-white/60 text-lg leading-relaxed max-w-xl">
                Six practice areas. One integrated team. Every engagement is tailored to your specific growth challenge — not a packaged off-the-shelf solution.
              </p>
            </div>
          </div>
        </section>

        {/* ── SERVICES GRID ── */}
        <section id="services" className="py-24 px-6 md:px-12" style={{ maxWidth: 'var(--container-max)', margin: '0 auto' }}>
          <div className="space-y-6">
            {servicesList.map((service, i) =>
            <article
              key={service.id}
              id={service.id}
              itemScope
              itemType="https://schema.org/Service"
              className={`reveal reveal-delay-${i % 3 + 1} grid grid-cols-1 md:grid-cols-2 gap-0 rounded-2xl overflow-hidden border border-border ${
              service.accent ? 'bg-primary text-white' : 'bg-card'}`
              }>
              
                <meta itemProp="name" content={service.title} />
                <meta itemProp="description" content={service.description} />
                <meta itemProp="provider" content="BusinessSite" />

                {/* Content */}
                <div
                className={`p-10 md:p-14 flex flex-col justify-between ${
                i % 2 === 0 ? 'md:order-1' : 'md:order-2'}`
                }>
                
                  <div>
                    <div className="flex items-center gap-4 mb-6">
                      <div
                      className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      service.accent ? 'bg-white/10' : 'bg-background border border-border'}`
                      }>
                      
                        <Icon
                        name={service.icon as any}
                        size={22}
                        className={service.accent ? 'text-accent' : 'text-accent'} />
                      
                      </div>
                      <span
                      className={`text-xs font-bold tracking-widest uppercase ${
                      service.accent ? 'text-white/40' : 'text-muted'}`
                      }>
                      
                        {service.number}
                      </span>
                    </div>
                    <h2
                    className={`text-3xl md:text-4xl font-display font-light mb-4 ${
                    service.accent ? 'text-white' : 'text-primary'}`
                    }
                    style={{ fontFamily: 'var(--font-display)' }}
                    itemProp="name">
                    
                      {service.title}
                    </h2>
                    <p
                    className={`leading-relaxed mb-8 ${
                    service.accent ? 'text-white/70' : 'text-muted'}`
                    }
                    itemProp="description">
                    
                      {service.description}
                    </p>
                    <ul className="grid grid-cols-2 gap-x-4 gap-y-2">
                      {service.features.map((f) =>
                    <li
                      key={f}
                      className={`flex items-center gap-2 text-sm font-medium ${
                      service.accent ? 'text-white/80' : 'text-primary'}`
                      }>
                      
                          <span className="w-1.5 h-1.5 rounded-full bg-accent flex-shrink-0" />
                          {f}
                        </li>
                    )}
                    </ul>
                  </div>
                  <div className="mt-10">
                    <Link
                    href="/contact"
                    className={service.accent ? 'btn-ghost' : 'btn-outline'}>
                    
                      Enquire About This
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 7h10M8 3l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                    </Link>
                  </div>
                </div>

                {/* Image */}
                <div
                className={`relative overflow-hidden min-h-[300px] ${
                i % 2 === 0 ? 'md:order-2' : 'md:order-1'}`
                }>
                
                  <AppImage
                  src={service.img}
                  alt={service.imgAlt}
                  fill
                  className="object-cover transition-transform duration-700 hover:scale-105"
                  loading="lazy" />
                
                  {service.accent &&
                <div className="absolute inset-0 bg-primary/20" />
                }
                </div>
              </article>
            )}
          </div>
        </section>

        {/* ── PROCESS ── */}
        <section
          className="py-24 px-6 md:px-12"
          style={{ background: 'var(--color-secondary)', maxWidth: '100%' }}>
          
          <div style={{ maxWidth: 'var(--container-max)', margin: '0 auto' }}>
            <div className="text-center mb-16 reveal">
              <span className="section-label mb-4 block">How We Work</span>
              <h2
                className="fluid-h2 font-display font-light text-primary"
                style={{ fontFamily: 'var(--font-display)' }}>
                
                Our process
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-5 gap-6 md:gap-4">
              {processSteps.map((step, i) =>
              <div
                key={step.num}
                className={`reveal reveal-delay-${i + 1} relative`}>
                
                  {/* Connector line */}
                  {i < processSteps.length - 1 &&
                <div className="hidden md:block absolute top-6 left-[calc(50%+24px)] right-0 h-px bg-border z-0" />
                }
                  <div className="relative z-10 flex flex-col items-center md:items-start text-center md:text-left">
                    <div
                    className="w-12 h-12 rounded-full border-2 border-accent flex items-center justify-center mb-5 bg-background">
                    
                      <span className="text-xs font-bold text-accent tracking-wider">{step.num}</span>
                    </div>
                    <h3
                    className="text-lg font-display font-semibold text-primary mb-2"
                    style={{ fontFamily: 'var(--font-display)' }}>
                    
                      {step.title}
                    </h3>
                    <p className="text-sm text-muted leading-relaxed">{step.desc}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* ── HARD CTA ── */}
        <section className="py-24 px-6 md:px-12 text-center reveal">
          <div style={{ maxWidth: '600px', margin: '0 auto' }}>
            <span className="section-label mb-4 block">Get Started</span>
            <h2
              className="fluid-h2 font-display font-light text-primary mb-6"
              style={{ fontFamily: 'var(--font-display)' }}>
              
              Ready to work together?
            </h2>
            <p className="text-muted leading-relaxed mb-10">
              Tell us about your project and we'll get back to you within 24 hours with a tailored proposal.
            </p>
            <Link href="/contact" className="btn-primary">
              Start a Conversation
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 7h10M8 3l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </Link>
          </div>
        </section>

      </main>

      <Footer />
    </>);

}